// Circular-arc edges (bulge form). Pure. See types.ts for the bulge convention.
//
// DECISIONS
// - Angles are radians in plan space (y up), counter-clockwise positive, so `sweep` carries the bulge sign.
// - `tessellateEdge` returns the points from a to b INCLUSIVE, with the endpoints exact (never recomputed
//   from trig), and splits an arc into equal angle steps: the step count depends only on radius, sweep and
//   tolerance, so every caller that tessellates the same edge gets the same points.
// - An arc is never approximated by fewer than 2 segments, so a curved edge cannot collapse into its chord.

import type { PlanPoint } from './types.ts';

/** A chord shorter than this is treated as a point. */
const MIN_CHORD = 1e-9;

export type Arc = {
    center: PlanPoint;
    radius: number;
    /** Angle of `a` around the center. */
    startAngle: number;
    /** Signed: positive = counter-clockwise, |sweep| <= PI for |bulge| <= 1. */
    sweep: number;
};

const chordOf = (a: PlanPoint, b: PlanPoint): number => Math.hypot(b.x - a.x, b.y - a.y);

/** The arc of a curved edge, or null for a straight edge (bulge 0) or a zero-length chord. */
export function arcOf(a: PlanPoint, b: PlanPoint, bulge: number): Arc | null {
    const chord = chordOf(a, b);
    if (!bulge || chord < MIN_CHORD) {
        return null;
    }
    const sweep = 4 * Math.atan(bulge);
    const radius = (chord * (1 + bulge * bulge)) / (4 * Math.abs(bulge));
    // Center sits on the perpendicular bisector, on the left of a -> b for a positive bulge.
    const offset = ((chord / 2) * (1 - bulge * bulge)) / (2 * bulge);
    const nx = -(b.y - a.y) / chord;
    const ny = (b.x - a.x) / chord;
    const center = { x: (a.x + b.x) / 2 + nx * offset, y: (a.y + b.y) / 2 + ny * offset };
    return { center, radius, startAngle: Math.atan2(a.y - center.y, a.x - center.x), sweep };
}

/** Height of the arc above its chord, mm. */
export function sagitta(chord: number, bulge: number): number {
    return (chord * Math.abs(bulge)) / 2;
}

/**
 * The bulge of the minor arc of `radius` over `chord`; positive when `outward`. A radius smaller than half
 * the chord cannot span it and yields the semicircle (|bulge| = 1).
 */
export function bulgeForRadius(chord: number, radius: number, outward: boolean): number {
    if (!(chord > 0) || !(radius > 0)) {
        return 0;
    }
    const half = chord / (2 * radius);
    const bulge = half >= 1 ? 1 : Math.tan((2 * Math.asin(half)) / 4);
    return outward ? bulge : -bulge;
}

/** Arc length of the edge, mm; the chord for a straight edge. */
export function edgeLength(a: PlanPoint, b: PlanPoint, bulge: number): number {
    const arc = arcOf(a, b, bulge);
    return arc ? arc.radius * Math.abs(arc.sweep) : chordOf(a, b);
}

/** Point at fraction `t` (0..1) along the edge by arc angle; the endpoints are returned exactly. */
export function arcPoint(a: PlanPoint, b: PlanPoint, bulge: number, t: number): PlanPoint {
    if (t <= 0) return { x: a.x, y: a.y };
    if (t >= 1) return { x: b.x, y: b.y };
    const arc = arcOf(a, b, bulge);
    if (!arc) {
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    const angle = arc.startAngle + arc.sweep * t;
    return { x: arc.center.x + arc.radius * Math.cos(angle), y: arc.center.y + arc.radius * Math.sin(angle) };
}

/** Number of segments a curved edge is cut into so no segment strays more than `chordTolMm` from the arc. */
export function arcSegmentCount(arc: Arc, chordTolMm: number, maxSegs: number): number {
    const ratio = Math.min(1, Math.max(0, chordTolMm / arc.radius));
    const step = 2 * Math.acos(1 - ratio);
    const wanted = step > 0 ? Math.ceil(Math.abs(arc.sweep) / step) : maxSegs;
    return Math.min(maxSegs, Math.max(2, wanted));
}

/** Points from a to b inclusive; [a, b] for a straight edge. */
export function tessellateEdge(a: PlanPoint, b: PlanPoint, bulge: number, chordTolMm = 20, maxSegs = 64): PlanPoint[] {
    const arc = arcOf(a, b, bulge);
    if (!arc) {
        return [
            { x: a.x, y: a.y },
            { x: b.x, y: b.y }
        ];
    }
    const segments = arcSegmentCount(arc, chordTolMm, maxSegs);
    const points: PlanPoint[] = [{ x: a.x, y: a.y }];
    for (let i = 1; i < segments; i += 1) {
        const angle = arc.startAngle + (arc.sweep * i) / segments;
        points.push({ x: arc.center.x + arc.radius * Math.cos(angle), y: arc.center.y + arc.radius * Math.sin(angle) });
    }
    points.push({ x: b.x, y: b.y });
    return points;
}
